'use client'

import Link from 'next/link'

import { Audio } from '@/components/client/audio/audio'
import { useTranslation } from '@/hooks/client/use-translation'
import type { DirectoryRow } from '@/lib/directory/row'

import { CompareButton } from './compare-button'
import { RowPrice } from './row-price'

interface RowFootProps {
  release: Pick<DirectoryRow, 'slug' | 'fullName' | 'fromPrice' | 'offer' | 'quietAudioUrl'>
  href: string
  isCompared: boolean
  atMax: boolean
  onToggleCompare: (slug: string) => void
}

// Price (the seller's offer, when there is one) on the left; review link, lab
// sample and the compare toggle on the right.
export const RowFoot = ({ release, href, isCompared, atMax, onToggleCompare }: RowFootProps) => {
  const { t } = useTranslation()

  return (
    <div className="border-navy/10 col-[1/4] row-start-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t pt-3 md:pt-4">
      <RowPrice release={release} />
      {/* ml-auto keeps the actions on the right when the foot wraps on phones. */}
      <div className="ml-auto flex items-center gap-3 sm:gap-4">
        <Link
          href={href}
          prefetch={false}
          data-tracking="ignore"
          className="text-lapis hover:text-navy hidden text-[14px] font-medium underline decoration-dotted underline-offset-4 sm:inline"
        >
          {t('directory.row.readReview', 'Read review')}
        </Link>
        {release.quietAudioUrl ? (
          <span className="flex items-center gap-1.5">
            <Audio
              url={release.quietAudioUrl}
              variant="button"
              group="directory"
              className="focus-visible:outline-lapis w-9 shadow-none focus-visible:outline-2 focus-visible:outline-offset-2"
            />
            <span className="text-navy/70 hidden text-[13px] sm:inline">{t('directory.row.listen', 'Listen')}</span>
          </span>
        ) : null}
        <CompareButton
          selected={isCompared}
          disabled={!isCompared && atMax}
          name={release.fullName}
          onToggle={() => onToggleCompare(release.slug)}
        />
      </div>
    </div>
  )
}
